// --- Ad group optimization / ROAS fields ---
export const OPTIMIZATION_TYPE_EVENT_BASED_ROAS = 'event-based-roas';
export const OPTIMIZATION_TYPE_REVENUE_BASED_ROAS = 'revenue-based-roas';
export const OPTIMIZATION_TYPE_OPTIONS = [
  { label: 'Event-Based ROAS', value: OPTIMIZATION_TYPE_EVENT_BASED_ROAS },
  { label: 'Revenue-Based ROAS', value: OPTIMIZATION_TYPE_REVENUE_BASED_ROAS }
];

export const ATTRIBUTION_WINDOW_DAYS = [7, 14, 30, 60, 90];
export const ATTRIBUTION_WINDOW_OPTIONS = ATTRIBUTION_WINDOW_DAYS.map(days => ({
  label: `${days} days`,
  value: `${days}`
}));

export const SPEND_SCOPE_AD_GROUP = 'ad-group';
export const SPEND_SCOPE_CAMPAIGN = 'campaign';

export const COGS_TYPE_DOLLAR = 'dollar';
export const COGS_TYPE_PERCENTAGE = 'percentage';

// Target ROAS Floor accepts either a ratio (e.g. "2:1") or a percentage
// (e.g. "200%" / "200") on input, but is always stored as a decimal ratio (e.g. 2.0 == 200%).
// Returns a finite number (the decimal ratio) or null when the input is empty/invalid.
export const parseRoasToPercentage = input => {
  if (input === null || input === undefined) return null;
  const raw = `${input}`.trim();
  if (raw === '') return null;

  if (raw.includes(':')) {
    const [numerator, denominator] = raw.split(':').map(p => Number(p.trim()));
    if (
      Number.isFinite(numerator) &&
      Number.isFinite(denominator) &&
      denominator !== 0
    ) {
      const ratio = numerator / denominator;
      // A ROAS floor cannot be negative.
      return ratio < 0 ? null : ratio;
    }
    return null;
  }

  const numeric = Number(raw.replace('%', '').trim());
  // A ROAS floor cannot be negative.
  if (!Number.isFinite(numeric) || numeric < 0) return null;
  return numeric / 100;
};

// Formats a stored ROAS decimal ratio for display (e.g. 2.5 -> "250%").
export const formatRoasPercentage = value => {
  if (value === null || value === undefined || value === '') return '';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '';
  return `${Math.round(numeric * 100)}%`;
};

// COGS Value is entered/displayed in the unit implied by COGS Type: a flat
// dollar amount for "dollar", or a percentage for "percentage". A percentage is
// stored as a decimal fraction (e.g. 20% -> 0.2); a dollar amount is stored
// as-is. The *100 / /100 round-trips can introduce float noise (0.07 * 100 ===
// 7.000000000000001), so trim it.
const stripFloatNoise = n => Number(n.toFixed(6));

// Stored value -> display value for the given COGS Type. Returns '' for empty.
export const formatCogsValue = (storedValue, cogsType) => {
  if (storedValue === null || storedValue === undefined || storedValue === '')
    return '';
  const numeric = Number(storedValue);
  if (!Number.isFinite(numeric)) return '';
  return cogsType === COGS_TYPE_PERCENTAGE
    ? stripFloatNoise(numeric * 100)
    : numeric;
};

// Display value -> stored value for the given COGS Type. Returns null for empty.
export const parseCogsValue = (displayValue, cogsType) => {
  if (
    displayValue === null ||
    displayValue === undefined ||
    displayValue === ''
  )
    return null;
  const numeric = Number(displayValue);
  if (!Number.isFinite(numeric)) return null;
  return cogsType === COGS_TYPE_PERCENTAGE
    ? stripFloatNoise(numeric / 100)
    : numeric;
};

// Dollar / count amounts must be a number that is zero or greater.
export const validateNonNegativeAmount = val => {
  if (val === '' || val === null || val === undefined) return null;
  const numeric = Number(val);
  if (!Number.isFinite(numeric)) return 'Enter a valid number.';
  if (numeric < 0) return 'Enter a value of 0 or greater.';
  return null;
};

// Percentages must be a number between 0 and 100
export const validatePercentage = val => {
  if (val === '' || val === null || val === undefined) return null;
  const numeric = Number(val);
  if (!Number.isFinite(numeric)) return 'Enter a valid number.';
  if (numeric < 0 || numeric > 100)
    return 'Enter a percentage between 0 and 100.';
  return null;
};

// `targeting.sourceIds` is the copy serving actually reads; the legacy `filter` blob
// is only a fallback for ad groups not re-saved since targeting was introduced (those
// have empty targeting arrays, so an empty value must never override the filter).
export const resolveSourceIds = (serverTargeting, parsedFilter) => ({
  sourceIdValues: serverTargeting?.sourceIds?.include?.length
    ? [...serverTargeting.sourceIds.include]
    : parsedFilter.sourceIdValues,
  excludedSourceIdValues: serverTargeting?.sourceIds?.exclude?.length
    ? [...serverTargeting.sourceIds.exclude]
    : parsedFilter.excludedSourceIdValues
});

export const buildTargeting = targeting => {
  if (!targeting) {
    return null;
  }

  const {
    sourceIdValues,
    excludedSourceIdValues,
    audienceMeet,
    excludedAudienceMeet,
    verticalIds,
    excludedVerticalIds,
    audienceIds,
    excludedAudienceIds,
    deviceTypes,
    locationIds,
    excludedLocationIds,
    zipCodes,
    excludedZipCodes,
    sessionAttributes,
    excludedSessionAttributes,
    attributesMeet,
    excludedAttributesMeet,
    profileAttributes,
    excludedProfileAttributes,
    profileAttributesApply,
    excludedProfileAttributesApply
  } = targeting;

  const newTargeting = {
    os: buildOSTargeting(deviceTypes),
    sourceIds: {
      include: sourceIdValues ?? null,
      exclude: excludedSourceIdValues ?? null
    },
    verticals: {
      include: verticalIds.length ? verticalIds : null,
      exclude: excludedVerticalIds.length ? excludedVerticalIds : null
    },
    audience: {
      includeMatchMode: audienceMeet,
      excludeMatchMode: excludedAudienceMeet,
      include: audienceIds.length ? audienceIds : null,
      exclude: excludedAudienceIds.length ? excludedAudienceIds : null
    },
    locations: {
      include: locationIds.length ? locationIds : null,
      exclude: excludedLocationIds.length ? excludedLocationIds : null
    },
    postalCodes: {
      include: zipCodes.length ? zipCodes : null,
      exclude: excludedZipCodes.length ? excludedZipCodes : null
    },
    profileAttributeConditions: {
      includeMatchMode: profileAttributesApply,
      excludeMatchMode: excludedProfileAttributesApply,
      include: buildProfileAttribute(profileAttributes),
      exclude: buildProfileAttribute(excludedProfileAttributes)
    },
    sessionAttributes: {
      includeMatchMode: attributesMeet,
      excludeMatchMode: excludedAttributesMeet,
      device: buildSessionAttribute(
        'DEVICE',
        sessionAttributes,
        excludedSessionAttributes
      ),
      queryParams: buildSessionAttribute(
        'QUERY_PARAM',
        sessionAttributes,
        excludedSessionAttributes
      ),
      profile: buildSessionAttribute(
        'PROFILE',
        sessionAttributes,
        excludedSessionAttributes
      ),
      partnerAttributes: buildSessionAttribute(
        'PARTNER',
        sessionAttributes,
        excludedSessionAttributes
      ),
      inferredAttributes: buildSessionAttribute(
        'INFERRED_ATTRIBUTES',
        sessionAttributes,
        excludedSessionAttributes
      )
    }
  };

  return newTargeting;
};

const buildOSTargeting = deviceTypes => {
  return deviceTypes.map(({ value, comparisonOperator, propertyValues }) => {
    const versionValue = propertyValues?.length ? propertyValues[0] : null;
    const COMPARISON_OPERATORS = {
      Equal: {
        minVersion: versionValue,
        maxVersion: versionValue
      },
      GreaterThanOrEqual: {
        minVersion: versionValue,
        maxVersion: null
      },
      LessThanOrEqual: {
        minVersion: null,
        maxVersion: versionValue
      }
    };
    return {
      ...COMPARISON_OPERATORS[comparisonOperator],
      platformName: value
    };
  });
};

const buildSessionAttribute = (
  attributeType,
  includedAttributes,
  excludedAttributes
) => {
  const mappedIncluded = includedAttributes
    .filter(a => a._type === attributeType)
    .map(sessionAttribute => {
      const {
        comparisonOperator,
        inverse,
        propertyName,
        propertyValues
      } = sessionAttribute;
      return {
        key: propertyName,
        values: propertyValues,
        comparisonStrategy: comparisonOperator,
        comparisonInverse: inverse
      };
    });

  const mappedExcluded = excludedAttributes
    .filter(a => a._type === attributeType)
    .map(sessionAttribute => {
      const {
        comparisonOperator,
        inverse,
        propertyName,
        propertyValues
      } = sessionAttribute;
      return {
        key: propertyName,
        values: propertyValues,
        comparisonStrategy: comparisonOperator,
        comparisonInverse: inverse
      };
    });
  return { include: mappedIncluded, exclude: mappedExcluded };
};

const buildProfileAttribute = attributes => {
  return attributes.map(attribute => {
    if (attribute.hasOwnProperty('fieldKey')) {
      const data = {
        key: attribute.fieldKey,
        values: attribute.selectedAttributeValues,
        comparisonStrategy:
          attribute.selectedDatatype === 'DoesNotExist'
            ? 'Exists'
            : attribute.selectedDatatype,
        comparisonInverse:
          attribute.selectedDatatype === 'DoesNotExist' ? true : false
      };
      return data;
    }
    return attribute;
  });
};
